import { UserCheck, ClipboardCheck, GraduationCap, RefreshCw, Eye, HeartHandshake, Star, Lock, MessageSquare, CheckCircle2, Loader2 } from "lucide-react";
import { formatClientAddress } from "@/lib/honorific";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = [UserCheck, ClipboardCheck, GraduationCap, RefreshCw, Eye, HeartHandshake];

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Portuguese money format with € suffix and 2 decimals (e.g. "2.105,94 €").
// Uses a manual formatter to guarantee the "." thousands separator regardless
// of the browser's Intl implementation (Chromium/pt-PT can drop it below 10k).
const fmtMoney = (n) => {
  const num = Number(n) || 0;
  const [intPart, decPart] = num.toFixed(2).split(".");
  const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${intWithDots},${decPart} €`;
};
// Whole number (e.g. days/week: "7" — never "7,00")
const fmtInt = (n) => `${Math.round(Number(n) || 0)}`;
// Hours with a leading space before the unit (e.g. "12 h" or "6,5 h")
const fmtHours = (n) => {
  const v = Number(n) || 0;
  const s = Number.isInteger(v)
    ? String(v)
    : v.toLocaleString("pt-PT", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return `${s} h`;
};

/**
 * A block renderer used both by the admin live preview and the public
 * proposal page. It receives an already-rendered block (placeholders expanded
 * by the backend). Some blocks are "dataOnly" — they don't accept new content
 * but pull directly from the lead payload.
 */
export function BlockRenderer({ block, data, forms }) {
  if (!block) return null;
  const c = block.content || {};
  switch (block.type) {
    case "hero": return <Hero c={c} contactName={data?.qualification?.contact?.name} />;
    case "text": return <TextBlock c={c} />;
    case "features_grid": return <FeaturesGrid c={c} data={data} />;
    case "image": return <ImageBlock c={c} />;
    case "image_text": return <ImageText c={c} />;
    case "beneficiaries_card": return <BeneficiariesCard c={c} data={data} />;
    case "pricing_card": return <PricingCard c={c} data={data} />;
    case "gestor_card": return <GestorCard c={c} data={data} />;
    case "deposit_card": return <DepositCard c={c} data={data} />;
    case "timeline_list": return <TimelineList c={c} data={data} />;
    case "testimonials_cta": return <TestimonialsCTA c={c} data={data} />;
    case "faq_list": return <FAQList c={c} data={data} />;
    case "observations_form": return <ObservationsForm c={c} data={data} forms={forms} />;
    case "acceptance_form": return <AcceptanceForm c={c} data={data} forms={forms} />;
    case "spacer": return <div style={{ height: Number(c.height) || 40 }} />;
    default: return <div className="text-xs text-red-500 p-4">Tipo desconhecido: {block.type}</div>;
  }
}

// ================== BLOCK COMPONENTS ==================

function Kicker({ children }) {
  if (!children) return null;
  return <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-4">{children}</div>;
}

function Hero({ c, contactName }) {
  const recipient = c.recipient || contactName || "";
  return (
    <section className="relative overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
        {c.kicker && <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--warning))] font-semibold mb-5">{c.kicker}</div>}
        {recipient && <div className="font-display text-4xl mb-4 text-primary">{recipient}</div>}
        {(c.title_line1 || c.title_italic) && (
          <h1 className="font-display text-3xl sm:text-5xl lg:text-7xl leading-[1.05] sm:leading-[1.02] tracking-tight max-w-4xl text-primary">
            {c.title_line1}
            {(c.title_line2_prefix || c.title_italic || c.title_line2_suffix) && <br />}
            {c.title_line2_prefix}
            {c.title_italic && <span className="italic text-[hsl(var(--warning))]">{c.title_italic}</span>}
            {c.title_line2_suffix}
          </h1>
        )}
        {c.subtitle && (
          <p className="text-foreground text-lg max-w-2xl mt-8 leading-relaxed whitespace-pre-wrap">{c.subtitle}</p>
        )}
      </div>
    </section>
  );
}

function TextBlock({ c }) {
  if (c.layout === "full") {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
        <Kicker>{c.kicker}</Kicker>
        {c.title && <h2 className="font-display text-3xl lg:text-4xl leading-tight text-primary mb-6">{c.title}</h2>}
        {c.body && <p className="text-foreground text-lg leading-relaxed whitespace-pre-wrap">{c.body}</p>}
      </section>
    );
  }
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <Kicker>{c.kicker}</Kicker>
        {c.title && <h2 className="font-display text-3xl lg:text-4xl leading-tight text-primary">{c.title}</h2>}
      </div>
      <div className="md:col-span-8 space-y-6">
        {c.body && <p className="text-foreground text-lg leading-relaxed whitespace-pre-wrap">{c.body}</p>}
        {(c.card_title || c.card_body) && (
          <div className="bg-card border border-border rounded-2xl p-5 sm:p-8 shadow-sm">
            {c.card_title && <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-3">{c.card_title}</div>}
            {c.card_body && <p className="text-foreground leading-relaxed whitespace-pre-wrap">{c.card_body}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturesGrid({ c, data }) {
  const proposal = data?.proposal || {};
  let items = c.items || [];
  if (c.source === "proposal.caring_advantages") {
    items = (proposal.caring_advantages || []).map((a, i) => (typeof a === "string" ? { title: "", description: a } : a));
  }
  if (!items.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
      <Kicker>{c.kicker}</Kicker>
      {c.title && <h2 className="font-display text-4xl lg:text-5xl leading-tight text-primary mb-14 max-w-3xl">{c.title}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((it, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <div key={i} className="bg-card border border-border rounded-2xl p-5 sm:p-8 hover:border-[hsl(var(--warning))]/30 transition-all group flex gap-5">
              {c.numbered ? (
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))] flex items-center justify-center font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</div>
              ) : (
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/20 flex items-center justify-center shrink-0 group-hover:bg-[hsl(var(--warning))]/20 transition-colors">
                  <Icon className="w-5 h-5 text-[hsl(var(--warning))]" strokeWidth={1.5} />
                </div>
              )}
              <div>
                {it.title && <h4 className="font-ui text-lg mb-1.5 text-primary">{it.title}</h4>}
                {it.description && <p className="text-foreground text-sm leading-relaxed">{it.description}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function toAbsoluteUrl(u) {
  if (!u) return "";
  if (/^https?:/i.test(u)) return u;
  return `${BACKEND_URL}${u.startsWith("/") ? u : "/" + u}`;
}

function ImageBlock({ c }) {
  if (!c.image_url) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 text-center text-sm text-muted-foreground italic border border-dashed border-border rounded-2xl">Bloco de imagem — carregue uma imagem no editor.</div>;
  }
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <img src={toAbsoluteUrl(c.image_url)} alt={c.alt || ""} className="w-full rounded-2xl object-cover" style={{ maxHeight: c.max_height ? `${c.max_height}px` : undefined }} />
      {c.caption && <div className="text-xs text-muted-foreground mt-3 text-center italic">{c.caption}</div>}
    </section>
  );
}

function ImageText({ c }) {
  const isLeft = c.image_position !== "right";
  const img = c.image_url ? (
    <img src={toAbsoluteUrl(c.image_url)} alt="" className="w-full rounded-2xl object-cover h-full" />
  ) : (
    <div className="w-full h-64 rounded-2xl bg-[hsl(var(--info-bg))] border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm italic">Sem imagem</div>
  );
  const txt = (
    <div>
      <Kicker>{c.kicker}</Kicker>
      {c.title && <h2 className="font-display text-3xl lg:text-4xl leading-tight text-primary mb-6">{c.title}</h2>}
      {c.body && <p className="text-foreground leading-relaxed whitespace-pre-wrap">{c.body}</p>}
    </div>
  );
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
      {isLeft ? <>{img}{txt}</> : <>{txt}{img}</>}
    </section>
  );
}

function BeneficiariesCard({ c, data }) {
  const q = data?.qualification || {};
  const cp1 = q.cared_person || {};
  const cp2 = q.cared_person_2 || {};
  const has2 = !!q.has_second_beneficiary;
  const list = [];
  if (cp1.name) list.push({ n: cp1.name, age: cp1.age, sex: cp1.sex });
  if (has2 && cp2.name) list.push({ n: cp2.name, age: cp2.age, sex: cp2.sex });
  if (!list.length) return null;
  const title = c.title || (list.length > 1 ? "Clientes" : "Cliente");
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <Kicker>{c.kicker}</Kicker>
      <h2 className="font-display text-3xl lg:text-4xl leading-tight text-primary mb-8">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((p, i) => (
          <div key={i} className="bg-card border border-border rounded-2xl p-6">
            {list.length > 1 && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-2">CLIENTE {i + 1}</div>
            )}
            <div className="font-display text-2xl text-primary">{formatClientAddress(p.n, p.sex)}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {p.age && <span>{p.age} anos</span>}
              {p.age && p.sex && <span> · </span>}
              {p.sex === "M" && <span>Masculino</span>}
              {p.sex === "F" && <span>Feminino</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PricingCard({ c, data }) {
  const p = data?.proposal || {};
  const options = p.options || [];
  const primary = options.find((o) => o.is_primary) || options[0];
  if (!primary) return null;
  const others = options.filter((o) => o !== primary);
  const isP = (primary?.mode || p.continuity_type) === "pontual";
  const priceValue = isP ? (primary?.total_value || p.monthly_price_min) : (primary?.monthly_avg || p.monthly_price_min);
  // 25/02/2026 — Redesign premium do card "Serviço Proposto" (pedido do
  // utilizador): tipografia sans-serif moderna sobre gradiente azul, número
  // grande e destacado, labels uppercase com tracking largo, formatação PT
  // consistente (€ com 2 casas, dias/horas sem casas). NENHUMA lógica de
  // cálculo foi alterada — apenas a apresentação visual.
  const priceLabel = isP ? "Valor total" : "Média mensal";
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div
        className="rounded-[28px] text-white px-6 py-10 sm:px-12 sm:py-14 lg:px-16"
        style={{
          background: "linear-gradient(135deg, #31458f 0%, #263875 100%)",
          boxShadow: "0 18px 45px rgba(14, 25, 55, 0.22)",
        }}
        data-testid="pricing-card"
      >
        {/* Kicker — identificação da modalidade em laranja Caring */}
        <div
          className="font-bold uppercase mb-8 sm:mb-10 text-[12px] sm:text-[14px]"
          style={{ color: "#ffb15c", letterSpacing: "0.22em" }}
          data-testid="pricing-card-kicker"
        >
          {c.kicker || "Serviço Proposto"}{primary?.name ? ` · ${primary.name}` : ""}
        </div>
        {/* Preço principal — elemento visual mais importante */}
        <div className="mb-8 sm:mb-10">
          <div
            className="text-white leading-[1.05]"
            style={{
              fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
              fontSize: "clamp(34px, 5.5vw, 44px)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
            }}
            data-testid="pricing-card-price"
          >
            {fmtMoney(priceValue)}
          </div>
          <div
            className="mt-2 font-medium"
            style={{
              color: "rgba(255,255,255,0.70)",
              fontSize: "clamp(14px, 1.5vw, 16px)",
            }}
          >
            {priceLabel}
          </div>
        </div>
        {/* Linha separadora subtil */}
        <div className="mb-8 sm:mb-10" style={{ height: 1, background: "rgba(255,255,255,0.22)" }} />
        {/* Grelha de indicadores — 4 colunas desktop, 2 tablet, 1/2 mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-testid="pricing-card-stats">
          {isP ? (
            <>
              <Stat label="Dias efetivos" v={fmtInt(primary?.total_days || 0)} />
              <Stat label="Horas/dia" v={fmtHours(primary?.hours_per_day || 0)} />
              <Stat label="Preço/hora" v={fmtMoney(primary?.hourly_price || p.hourly_rate)} />
              <Stat label="Extra aplicado" v={fmtMoney(primary?.extra_fee || 0)} highlight />
            </>
          ) : (
            <>
              <Stat label="Dias/semana" v={fmtInt(primary?.days_per_week || 0)} />
              <Stat label="Horas/dia" v={fmtHours(primary?.hours_per_day || 0)} />
              <Stat label="Preço/hora" v={fmtMoney(primary?.hourly_price || p.hourly_rate)} />
              <Stat label="Valor semanal" v={fmtMoney(primary?.weekly_value || 0)} highlight />
            </>
          )}
        </div>
      </div>
      {p.qt_confirmed_by_name && (
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="qt-attest">
          <span className={p.qt_adjusted ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]"}>✓</span>
          <span>
            Valores {p.qt_adjusted ? "ajustados" : "confirmados"} na Reunião Técnica por <b className="text-primary">{p.qt_confirmed_by_name}</b>
            {p.qt_adjusted && p.qt_adjustment_reason && <span className="text-muted-foreground"> · {p.qt_adjustment_reason}</span>}
          </span>
        </div>
      )}
      {c.show_other_options && others.length > 0 && (
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-4">Outras opções que pode considerar</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {others.map((o) => {
              const oP = (o.mode || p.continuity_type) === "pontual";
              const oPrice = oP ? o.total_value : o.monthly_avg;
              const oPriceLabel = oP ? "Valor total" : "Média mensal";
              return (
                <div
                  key={o.id}
                  className="bg-card border border-border rounded-2xl p-6"
                  data-testid={`pricing-card-other-${o.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="font-ui text-lg text-primary">{o.name}</div>
                    <div className="text-right">
                      <div
                        className="text-primary"
                        style={{
                          fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                          fontSize: "clamp(20px, 2.4vw, 26px)",
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                          lineHeight: 1.1,
                        }}
                      >
                        {fmtMoney(oPrice)}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">{oPriceLabel}</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    {oP ? (<>
                      <span>{fmtInt(o.total_days)} dias</span>
                      <span>{fmtHours(o.hours_per_day)}/dia</span>
                      <span>{fmtMoney(o.hourly_price)}/h</span>
                      {o.extra_fee > 0 && <span>+{fmtMoney(o.extra_fee)} extra</span>}
                    </>) : (<>
                      <span>{fmtInt(o.days_per_week)} dias/sem</span>
                      <span>{fmtHours(o.hours_per_day)}/dia</span>
                      <span>{fmtMoney(o.hourly_price)}/h</span>
                    </>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {c.show_additional_notes && p.additional_notes && (
        <div className="mt-8 bg-card/60 border border-border rounded-2xl p-6 text-foreground text-sm leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            {p.additional_notes_by_name ? "Notas do Consultor" : "Notas do Caring"}
          </div>
          <div className="whitespace-pre-wrap">{p.additional_notes}</div>
          {p.additional_notes_by_name && (
            <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground italic">
              por <b className="not-italic text-foreground">{p.additional_notes_by_name}</b>
              {p.additional_notes_at && (() => {
                try {
                  const d = new Date(p.additional_notes_at);
                  return <> · {d.toLocaleDateString("pt-PT")}</>;
                } catch { return null; }
              })()}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
function Stat({ label, v, highlight }) {
  return (
    <div>
      <div
        className="font-bold"
        style={{
          color: "rgba(255,255,255,0.65)",
          fontSize: 12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="mt-3"
        style={{
          fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
          color: highlight ? "#ffbd70" : "#ffffff",
          fontSize: "clamp(22px, 3vw, 27px)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          lineHeight: 1.1,
        }}
      >
        {v}
      </div>
    </div>
  );
}

function GestorCard({ c, data }) {
  const mgr = data?.manager;
  if (!mgr || !mgr.name) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <Kicker>{c.kicker}</Kicker>
      {c.title && <h2 className="font-display text-3xl lg:text-4xl leading-tight text-primary mb-8">{c.title}</h2>}
      <div className="bg-card border border-border rounded-3xl p-5 sm:p-8 flex flex-col md:flex-row gap-6 items-start">
        <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-full overflow-hidden bg-primary text-primary-foreground flex items-center justify-center text-3xl font-display">
          {mgr.photo_url ? (
            <img src={toAbsoluteUrl(mgr.photo_url)} alt={mgr.name} className="w-full h-full object-cover" />
          ) : (
            (mgr.name || "?").charAt(0).toUpperCase()
          )}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-2">{mgr.title || "Gestor de Cliente"}</div>
          <div className="font-display text-2xl text-primary">{mgr.name}</div>
          {mgr.bio && <p className="text-foreground text-sm leading-relaxed mt-3">{mgr.bio}</p>}
          <div className="mt-4 text-sm text-muted-foreground space-x-4">
            {mgr.email && <span>{mgr.email}</span>}
            {mgr.phone && <span>{mgr.phone}</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

function DepositCard({ c, data }) {
  const dep = data?.deposit;
  if (!dep || !dep.total_amount) return null;
  const startDate = dep.service_start_date
    ? new Date(dep.service_start_date).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })
    : null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 pb-6" data-testid="deposit-section">
      <div className="rounded-xl border border-border bg-card/60 px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-baseline gap-2" data-testid="deposit-total">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Caução</span>
          <span className="font-ui text-lg text-primary font-semibold tabular-nums">{fmt(dep.total_amount)} €</span>
        </div>
        <span className="text-[#E2E8F0]">·</span>
        <div className="flex items-baseline gap-2" data-testid="deposit-schedule">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Arranque</span>
          <span className="font-ui text-base text-[hsl(var(--warning))] font-semibold tabular-nums">{fmt(dep.kickstart_amount)} €</span>
          <span className="text-[10px] text-muted-foreground">até 24h</span>
        </div>
        {dep.remainder_amount > 0 && (
          <>
            <span className="text-[#E2E8F0]">·</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Remanescente</span>
              <span className="font-ui text-base text-foreground font-semibold tabular-nums">{fmt(dep.remainder_amount)} €</span>
              <span className="text-[10px] text-muted-foreground">até 48h antes</span>
            </div>
          </>
        )}
        {startDate && (
          <>
            <span className="text-[#E2E8F0]">·</span>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Início</span>
              <span className="font-ui text-sm text-primary font-semibold">{startDate}</span>
            </div>
          </>
        )}
      </div>
      {c.footnote && (
        <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed max-w-3xl whitespace-pre-wrap">
          {c.footnote}
        </p>
      )}
    </section>
  );
}

function TimelineList({ c, data }) {
  const items = data?.proposal?.timeline || [];
  if (!items.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
      <Kicker>{c.kicker}</Kicker>
      {c.title && <h2 className="font-display text-4xl lg:text-5xl mb-14 leading-tight text-primary">{c.title}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {items.map((step, i) => (
          <div key={i} className="bg-card border border-border rounded-2xl p-6 relative">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-3">Fase {i + 1}</div>
            <div className="font-ui text-lg mb-2 text-primary">{step.phase}</div>
            <div className="text-muted-foreground text-sm leading-relaxed">{step.description}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialsCTA({ c, data }) {
  const url = data?.testimonials_url;
  if (!url) return null;
  return (
    <section className="bg-card py-16 border-y border-border" data-testid="testimonials-cta">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="flex justify-center gap-1 mb-4">
          {Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-5 h-5 text-[hsl(var(--warning))]" fill="#F59E0B" />)}
        </div>
        <Kicker>{c.kicker}</Kicker>
        {c.title && <h2 className="font-display text-3xl lg:text-4xl mb-4 leading-tight text-primary">{c.title}</h2>}
        {c.body && <p className="text-foreground max-w-2xl mx-auto mb-8 leading-relaxed">{c.body}</p>}
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-primary hover:bg-primary text-primary-foreground rounded-full px-8 py-3.5 text-sm font-semibold transition-colors" data-testid="testimonials-link">
          {c.button_label || "Ver testemunhos"} <span aria-hidden>→</span>
        </a>
      </div>
    </section>
  );
}

function FAQList({ c, data }) {
  const items = data?.proposal?.faq || [];
  if (!items.length) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
      <Kicker>{c.kicker}</Kicker>
      {c.title && <h2 className="font-display text-4xl lg:text-5xl mb-14 leading-tight text-primary">{c.title}</h2>}
      <div className="space-y-3">
        {items.map((f, i) => (
          <details key={i} className="group bg-card border border-border rounded-2xl p-6 open:border-[hsl(var(--warning))]/40 transition-colors">
            <summary className="cursor-pointer font-ui text-lg flex justify-between items-center list-none text-primary">
              {f.question}
              <span className="text-[hsl(var(--warning))] group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="text-foreground text-sm leading-relaxed mt-4">{f.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ObservationsForm({ c, data, forms }) {
  const accepted = !!data?.acceptance?.accepted;
  const f = forms?.observations || {};
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="observations-section">
      <div className="bg-card border border-border rounded-3xl p-5 sm:p-8 lg:p-12">
        <Kicker>{c.kicker}</Kicker>
        <h3 className="font-display text-3xl lg:text-4xl mb-4 leading-tight inline-flex items-center gap-3 text-primary">
          <MessageSquare className="w-7 h-7 text-[hsl(var(--warning))]" /> {accepted ? "Proposta aceite" : (c.title || "Alguma nota antes de aceitar?")}
        </h3>
        {accepted ? (
          <div className="rounded-2xl border border-border bg-background p-6 flex items-start gap-3" data-testid="observations-locked">
            <Lock className="w-5 h-5 text-[hsl(var(--warning))] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-ui text-foreground mb-1">Este link é agora só de consulta.</div>
              <div className="text-muted-foreground text-sm leading-relaxed">
                A partir do momento em que aceitou a proposta, deixou de ser possível submeter novas observações por aqui. Para quaisquer alterações ou dúvidas, por favor contacte a sua Gestora diretamente.
              </div>
            </div>
          </div>
        ) : (
          <>
            {c.body && <p className="text-muted-foreground mb-6 leading-relaxed max-w-2xl whitespace-pre-wrap">{c.body}</p>}
            {f.submitted ? (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 flex items-start gap-3" data-testid="observation-submitted">
                <CheckCircle2 className="w-6 h-6 text-[hsl(var(--success))] flex-shrink-0" />
                <div>
                  <div className="font-ui text-lg mb-1 text-primary">Observação enviada.</div>
                  <div className="text-muted-foreground text-sm">Obrigado. Contactá-lo-emos brevemente sobre este ponto.</div>
                  <button onClick={f.onReset} className="mt-3 text-xs text-[hsl(var(--warning))] hover:text-[hsl(var(--warning))] underline" data-testid="observation-add-another">
                    Adicionar outra observação
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <input value={f.obsName || ""} onChange={(e) => f.setObsName?.(e.target.value)} placeholder="O seu nome (opcional)"
                  className="w-full bg-background border border-border rounded-2xl px-5 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary"
                  data-testid="observation-name" />
                <textarea value={f.obsText || ""} onChange={(e) => f.setObsText?.(e.target.value)} rows={5}
                  placeholder={c.placeholder || "Escreva a sua observação..."} maxLength={5000}
                  className="w-full bg-background border border-border rounded-2xl px-5 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary resize-y min-h-[140px]"
                  data-testid="observation-text" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{(f.obsText || "").length}/5000</span>
                  <button onClick={f.onSubmit} disabled={f.sending || !(f.obsText || "").trim()}
                    className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="observation-submit">
                    {f.sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                    {c.submit_label || "Enviar observação"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function AcceptanceForm({ c, data, forms }) {
  const accepted = !!data?.acceptance?.accepted;
  const declined = data?.decline?.decision === "declined";
  const f = forms?.acceptance || {};
  // If the client has already definitively declined, hide the acceptance form entirely
  // so they don't see contradictory messages. Postpone keeps it visible.
  if (declined) return null;
  return (
    <section id="aceitacao" className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
      <div className="bg-gradient-to-br from-[hsl(var(--info-bg))] via-card to-[hsl(var(--info-bg))] border border-primary/20 rounded-3xl p-10 lg:p-16 shadow-sm">
        {accepted ? (
          <div className="text-center">
            <CheckCircle2 className="w-14 h-14 text-[hsl(var(--success))] mx-auto mb-6" />
            <h3 className="font-display text-4xl mb-4 text-primary">Proposta aceite.</h3>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              Obrigado pela sua confiança. Falta apenas um passo — preencher os dados de contrato para formalizarmos o serviço.
            </p>
            <button onClick={f.onGoContract} className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold rounded-full px-8 py-3.5 text-sm inline-flex items-center gap-2" data-testid="go-to-contract-data-btn">
              Continuar → Dados do contrato
            </button>
            {data?.acceptance?.accepted_at && (
              <div className="text-xs text-muted-foreground mt-8">Aceite por {data.acceptance.accepted_by_name} em {new Date(data.acceptance.accepted_at).toLocaleString("pt-PT")}</div>
            )}
          </div>
        ) : (
          <>
            <Kicker>{c.kicker}</Kicker>
            {c.title && <h3 className="font-display text-4xl lg:text-5xl mb-6 leading-tight text-primary">{c.title}</h3>}
            {c.body && <p className="text-foreground mb-8 max-w-2xl leading-relaxed whitespace-pre-wrap">{c.body}</p>}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 max-w-2xl">
              <input value={f.name || ""} onChange={(e) => f.setName?.(e.target.value)} placeholder={c.input_placeholder || "O seu nome completo"}
                data-testid="accept-name"
                className="bg-card border border-border rounded-full px-5 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary" />
              <button onClick={f.onAccept} disabled={f.accepting} data-testid="accept-btn"
                className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-primary-foreground font-semibold rounded-full px-8 py-3 disabled:opacity-60">
                {f.accepting ? "A processar..." : (c.button_label || "Aceitar Proposta")}
              </button>
            </div>
            {c.security_note && (
              <div className="flex items-center gap-2 mt-6 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5" /> {c.security_note}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
